/**
 * What the Carried panel's Coalesce section offers: standard recipes grouped
 * into rows by series (one per crystal type and dominant essence), whether a
 * carried slot can take a recipe's crystal (shortfalls are the shared
 * `resolveEssenceShortfall`). See
 * docs/empyreal-crystal-mechanics.md.
 */
import { canCoalesceIntoOccupant, toCrystalKind, type CrystalSlotOccupant } from '@sorrem/shared';
import type {
    EmpyrealCrystalSize,
    EmpyrealCrystalType,
    RecipeDefinition,
} from '@sorrem/shared';
import type { UICoalescence, UIInventoryItem } from '@sorrem-client/types';


/** One series of recipes: the same crystal type and essence, in each size. */
export interface CoalesceRow {
    /** Recipe id without its size, e.g. `vitality-courage`. */
    readonly key: string;
    /** Recipe name without its size, e.g. `Vitality (Courage)`. */
    readonly label: string;
    readonly crystalType: EmpyrealCrystalType;
    readonly recipes: Partial<Record<EmpyrealCrystalSize, RecipeDefinition>>;
}

function stripSize(value: string, size: EmpyrealCrystalSize, separator: string): string {
    return value.split(separator).filter((part) => part.toLowerCase() !== size).join(separator);
}

/**
 * Groups the standard (known from the start) recipes into series rows, in
 * the order they are registered. Hidden recipes are left out until recipe
 * discovery exists.
 */
export function buildCoalesceRows(recipes: readonly RecipeDefinition[]): CoalesceRow[] {
    const rows = new Map<string, { label: string; crystalType: EmpyrealCrystalType; recipes: Partial<Record<EmpyrealCrystalSize, RecipeDefinition>> }>();
    for (const recipe of recipes) {
        if (!recipe.isDiscoveredByDefault) {
            continue;
        }
        const key = stripSize(recipe.id, recipe.size, '-');
        const row = rows.get(key) ?? { label: stripSize(recipe.name, recipe.size, ' '), crystalType: recipe.crystalType, recipes: {} };
        row.recipes[recipe.size] = recipe;
        rows.set(key, row);
    }
    return Array.from(rows, ([key, row]) => ({ key, ...row }));
}

/**
 * Whether one more crystal from `recipe` fits in a carried slot once every
 * coalescence already headed there lands: the shared coalescing room rule
 * (`canCoalesceIntoOccupant`), over what the slot shows.
 *
 * @param item What occupies the slot now.
 * @param pending Coalescences targeting this slot.
 */
export function canCoalesceIntoSlot(
    item: UIInventoryItem | undefined,
    pending: readonly UICoalescence[],
    recipe: Pick<RecipeDefinition, 'id' | 'crystalType' | 'size'>,
): boolean {
    const occupant: CrystalSlotOccupant = item === undefined
        ? undefined
        : (item.crystal === undefined ? 'other' : { ...item.crystal, quantity: item.qty });
    const arriving = pending.map(({ recipeId, crystalType, size }) => ({ recipeId, crystalType, size }));
    return canCoalesceIntoOccupant(occupant, arriving, toCrystalKind(recipe));
}
